import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, formatApiError } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AdminSidebar } from "@/pages/AdminUsers";
import {
  Upload, FileSpreadsheet, CheckCircle2, Info, Library, Plus, Trash2, Download,
  Car, Caravan, Bike, Ship, Loader2, Tag, Eye, RefreshCw, Package, Layers, ExternalLink,
  Copy, History, ClipboardPaste, ChevronRight, ChevronLeft, ChevronDown, ChevronUp, Lock,
} from "lucide-react";
import ClassOverridesPanel from "@/components/ClassOverridesPanel";
import DecodoUsageCard from "@/components/DecodoUsageCard";

const VERTICALS = [
  { value: "automotive",  label: "Automotive",  icon: Car },
  { value: "rv",          label: "RV",          icon: Caravan },
  { value: "powersports", label: "Powersports", icon: Bike },
  { value: "marine",      label: "Marine",      icon: Ship },
];

const RATE_PROGRAMS = [
  {
    kind: "essentials",
    label: "Pre-Owned VSC — Independent (Essentials tiers)",
    hint: "Sheets: Essentials Powertrain, Essentials Plus, Essentials Enhanced, Essentials Exclusionary + Class Guide.",
  },
  {
    kind: "blended",
    label: "Blended VSC (single blended sheet)",
    hint: "Single sheet with Essentials / Essentials Plus / Essentials Enhanced / Essentials Exclusionary blocks, Term/Miles column, Class 1 & 2 / 3 / 4 columns, surcharges + Class Guide tab. Separate program from Pre-Owned VSC.",
  },
  {
    kind: "franchise",
    label: "Franchise VSC (Exclusionary)",
    hint: "Asian / Domestic / European tabs, or the raw system export (Model Group / Max month / Mile / Cost).",
  },
  {
    kind: "ancillary",
    label: "4n1 Ancillary Bundle",
    hint: "Sheets: Bundle, Tire and Wheel, Paintless Dent Repair, Key Replacement, Windshield.",
  },
  {
    kind: "wear_guard",
    label: "Wear Guard",
    hint: "Single sheet with Class A / Class B pricing and vehicle classifications.",
  },
  {
    kind: "gap",
    label: "GAP",
    hint: "Sheet with New Franchise / Independent / Buy Here Pay Here / Ford FLEX GAP pricing across LTV bands.",
  },
];

const PROGRAM_HUB = [
  { kind: "essentials",  label: "Pre-Owned VSC",     tone: "from-[#0B132B]/8 to-transparent" },
  { kind: "blended",     label: "Blended VSC",       tone: "from-[#0B132B]/12 to-transparent" },
  { kind: "franchise",   label: "Franchise VSC",     tone: "from-[#D4AF37]/12 to-transparent" },
  { kind: "ancillary",   label: "4n1 Ancillary",     tone: "from-blue-500/8 to-transparent" },
  { kind: "wear_guard",  label: "Wear Guard",        tone: "from-emerald-500/8 to-transparent" },
  { kind: "gap",         label: "GAP",               tone: "from-purple-500/8 to-transparent" },
  { kind: "reinsurance", label: "Reinsurance",       tone: "from-orange-500/8 to-transparent" },
];

export default function AdminRates() {
  const [info, setInfo] = useState(null);
  const [params, setParams] = useSearchParams();
  const vertical = params.get("vertical");
  const tab = params.get("tab") || "rates";
  const [sheets, setSheets] = useState([]);
  const [materials, setMaterials] = useState([]);

  const load = async () => {
    try {
      const [i, s, m] = await Promise.all([api.get("/admin/rates/info"), api.get("/admin/rate-library/sheets"), api.get("/admin/marketing/materials")]);
      setInfo(i.data); setSheets(s.data || []); setMaterials(m.data || []);
    } catch { toast.error("Failed to load programs"); }
  };
  useEffect(() => { load(); }, []);

  const open = (v, t) => setParams(v ? { vertical: v, tab: t || "rates" } : {}, { replace: false });
  const activeV = VERTICALS.find((v) => v.value === vertical);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="mb-10">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Administration</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-[#0B132B] mt-2 tracking-tight leading-none">Programs & rates</h1>
          <p className="text-sm text-[#6C757D] mt-3 max-w-xl leading-relaxed">One place per vertical: the live rate cards (they feed proposals and the rate-card generator automatically), the POS materials agents can order, and — for Advantage only — the admin fees behind each program.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
          <AdminSidebar />
          <div className="space-y-6">
            {!activeV ? (
              <div className="grid grid-cols-1 gap-3" data-testid="vertical-list">
                {VERTICALS.map((v) => {
                  const V = v.icon;
                  const vs = sheets.filter((s) => s.vertical === v.value && !s.production_only);
                  const live = vs.filter((s) => s.engine_kind).length;
                  const mats = materials.filter((m) => (m.vertical_slug || "automotive") === v.value).length;
                  return (
                    <button key={v.value} type="button" onClick={() => open(v.value)} className="group w-full text-left bg-white border border-[#E9ECEF] rounded-sm p-5 flex items-center gap-5 hover:border-[#D4AF37] hover:shadow-[0_6px_20px_rgba(11,19,43,0.06)] transition-[border-color,box-shadow]" data-testid={`vertical-card-${v.value}`}>
                      <span className="h-12 w-12 rounded-sm grid place-items-center bg-[#0B132B] text-[#D4AF37] shrink-0"><V size={22} /></span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-display text-xl font-black text-[#0B132B]">{v.label}</span>
                        <span className="block text-xs text-[#6C757D] mt-0.5">{vs.length} rate card{vs.length === 1 ? "" : "s"} · {live} live in engine · {mats} POS material{mats === 1 ? "" : "s"}</span>
                      </span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-sm border font-bold ${live ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-500"}`}>{live ? "RATES LIVE" : "NO RATES"}</span>
                      <ChevronRight size={18} className="text-[#6C757D] group-hover:text-[#0B132B] group-hover:translate-x-0.5 transition-transform" />
                    </button>
                  );
                })}
              </div>
            ) : (
              <div data-testid={`vertical-panel-${activeV.value}`}>
                <button type="button" onClick={() => open(null)} className="inline-flex items-center gap-1 text-xs font-bold text-[#6C757D] hover:text-[#0B132B] mb-3" data-testid="vertical-back"><ChevronLeft size={14} /> All verticals</button>
                <div className="flex items-center gap-3 mb-4">
                  <span className="h-10 w-10 rounded-sm grid place-items-center bg-[#0B132B] text-[#D4AF37]"><activeV.icon size={18} /></span>
                  <h2 className="font-display text-2xl font-black text-[#0B132B]">{activeV.label}</h2>
                </div>
                <div className="flex flex-wrap gap-1 border-b border-[#E9ECEF] mb-6" role="tablist">
                  {[["rates", "Rate cards"], ["materials", "POS materials"], ["fees", "Admin fees"], ["guides", "Class guides"], ...(activeV.value === "automotive" ? [["engine", "Engine details"]] : [])].map(([k, l]) => (
                    <button key={k} role="tab" aria-selected={tab === k} onClick={() => open(activeV.value, k)} className={`px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] border-b-2 -mb-px ${tab === k ? "border-[#D4AF37] text-[#0B132B]" : "border-transparent text-[#6C757D] hover:text-[#0B132B]"}`} data-testid={`vertical-tab-${k}`}>{l}</button>
                  ))}
                </div>
                {tab === "rates" && <RateLibrarySection vertical={activeV.value} onChanged={load} />}
                {tab === "materials" && <VerticalMaterials vertical={activeV} materials={materials.filter((m) => (m.vertical_slug || "automotive") === activeV.value)} />}
                {tab === "fees" && <AdminFeesTab vertical={activeV} />}
                {tab === "guides" && <ClassGuidesTab vertical={activeV} />}
                {tab === "engine" && <EngineDetails info={info} onChanged={load} />}
              </div>
            )}
            <DecodoUsageCard />
          </div>
        </div>
      </main>
    </div>
  );
}

function VerticalMaterials({ vertical, materials }) {
  return (
    <Card className="bg-white border border-[#E9ECEF] rounded-sm p-6" data-testid="vertical-materials">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">POS materials — {vertical.label}</div>
        <Link to={`/admin/marketing?vertical=${vertical.value}`}><Button size="sm" className="h-9 rounded-sm bg-[#0B132B] text-white" data-testid="vertical-materials-manage"><Upload size={12} className="mr-1.5" /> Add / manage materials</Button></Link>
      </div>
      {materials.length === 0 ? <div className="text-xs text-[#6C757D] italic py-2">No POS materials uploaded for {vertical.label} yet.</div> : (
        <ul className="divide-y divide-[#F1F3F5]">{materials.map((m) => (
          <li key={m.id} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`material-${m.id}`}>
            <div className="min-w-0"><div className="font-semibold text-[#0B132B] truncate">{m.title}</div><div className="text-[11px] text-[#6C757D]">{m.material_type || m.kind || "material"} · {m.program || "general"}{m.filename ? ` · ${m.filename}` : ""}</div></div>
            <Link to={`/admin/marketing?program=${m.program || "general"}`} className="text-xs font-bold text-[#0B132B] underline shrink-0">Open</Link>
          </li>))}</ul>)}
    </Card>
  );
}

function AdminFeesTab({ vertical }) {
  const [rows, setRows] = useState(null);
  const [draft, setDraft] = useState({});
  const [levelDraft, setLevelDraft] = useState({}); // {product_id: {tableName: value}}
  const [openLevels, setOpenLevels] = useState({});
  const load = async () => { try { const { data } = await api.get(`/admin/admin-fees?vertical=${vertical.value}`); setRows(data); setDraft({}); setLevelDraft({}); } catch { toast.error("Could not load admin fees"); } };
  useEffect(() => { load(); }, [vertical.value]); // eslint-disable-line react-hooks/exhaustive-deps
  const levelFees = (r) => {
    const merged = { ...(r.table_fees || {}), ...(levelDraft[r.product_id] || {}) };
    const out = {};
    Object.entries(merged).forEach(([k, v]) => { if (v !== "" && v !== undefined && v !== null && !Number.isNaN(Number(v))) out[k] = Number(v); });
    return out;
  };
  const save = async (r) => {
    const v = draft[r.product_id] ?? r.fee; if (v === undefined || v === "") return;
    try { await api.put(`/admin/admin-fees/${r.product_id}`, { fee: Number(v), table_fees: levelFees(r) }); toast.success(`${r.name}: admin fees saved`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const clear = async (r) => { try { await api.delete(`/admin/admin-fees/${r.product_id}`); toast.success(`${r.name}: admin fee cleared`); load(); } catch { toast.error("Clear failed"); } };
  const setLevel = (pid, name, v) => setLevelDraft((d) => ({ ...d, [pid]: { ...(d[pid] || {}), [name]: v } }));
  return (
    <Card className="bg-white border border-[#E9ECEF] rounded-sm p-6" data-testid="admin-fees-tab">
      <div className="flex items-center gap-2 mb-1"><Lock size={14} className="text-[#D4AF37]" /><div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">Advantage admin fees — {vertical.label}</div></div>
      <p className="text-xs text-[#6C757D] mb-4">Flat dollar amount per contract, per product. Super admins only. The fee is folded into the rate card cost automatically (base + admin fee + agent markup) — dealers and agents never see it itemised. Programs with several coverage levels can carry a different fee per level — open <b>Coverage levels</b>; blank levels use the standard fee. Override per dealer from the pipeline's Programs & Commissions step.</p>
      {!rows ? <div className="text-sm text-[#6C757D]">Loading…</div> : rows.length === 0 ? <div className="text-xs italic text-[#6C757D]">No portfolio products for {vertical.label}.</div> : (
        <table className="w-full text-sm"><thead><tr className="text-[10px] uppercase tracking-[0.14em] text-[#6C757D] border-b border-[#E9ECEF]"><th className="text-left py-2">Product</th><th className="text-left py-2">Standard admin fee</th><th className="py-2"></th></tr></thead>
          <tbody>{rows.map((r) => {
            const levelsOpen = !!openLevels[r.product_id];
            const setCount = Object.keys(r.table_fees || {}).length;
            return (
            <React.Fragment key={r.product_id}>
            <tr className="border-b border-[#F1F3F5]" data-testid={`fee-row-${r.slug}`}>
              <td className="py-2 pr-3">
                <div className="font-semibold text-[#0B132B]">{r.name}</div>
                <div className="text-[10px] uppercase tracking-[0.12em] text-[#6C757D]">{r.category || "—"}</div>
                {(r.levels || []).length > 1 && (
                  <button type="button" onClick={() => setOpenLevels((o) => ({ ...o, [r.product_id]: !levelsOpen }))} className="mt-1 text-[11px] font-semibold text-[#0B132B] hover:underline flex items-center gap-1" data-testid={`fee-levels-toggle-${r.slug}`}>
                    {levelsOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />} Coverage levels ({r.levels.length}){setCount ? ` · ${setCount} custom` : ""}
                  </button>
                )}
              </td>
              <td className="py-2 pr-3"><div className="flex items-center gap-2"><span className="text-[#6C757D]">$</span><Input type="number" step="0.01" min="0" value={draft[r.product_id] ?? r.fee} onChange={(e) => setDraft({ ...draft, [r.product_id]: e.target.value })} className="h-8 w-28 rounded-sm" data-testid={`fee-input-${r.slug}`} />{!r.is_set && <span className="text-[10px] text-[#6C757D] italic">not set</span>}</div></td>
              <td className="py-2 text-right whitespace-nowrap"><Button size="sm" onClick={() => save(r)} className="h-8 rounded-sm bg-[#0B132B] text-white mr-1" data-testid={`fee-save-${r.slug}`}>Save</Button>{r.is_set && <Button size="sm" variant="outline" onClick={() => clear(r)} className="h-8 rounded-sm" data-testid={`fee-clear-${r.slug}`}>Clear</Button>}</td>
            </tr>
            {levelsOpen && (
              <tr className="border-b border-[#F1F3F5] bg-[#F8F9FA]" data-testid={`fee-levels-${r.slug}`}>
                <td colSpan={3} className="py-2 px-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {r.levels.map((lvl) => {
                      const cur = levelDraft[r.product_id]?.[lvl] ?? (r.table_fees?.[lvl] ?? "");
                      return (
                        <div key={lvl} className="flex items-center justify-between gap-2 bg-white border border-[#E9ECEF] rounded-sm px-2.5 py-1.5">
                          <div className="text-xs font-semibold text-[#0B132B] truncate" title={lvl}>{lvl}</div>
                          <div className="flex items-center gap-1 shrink-0"><span className="text-[#6C757D] text-xs">$</span>
                            <Input type="number" step="0.01" min="0" placeholder={`${Number(draft[r.product_id] ?? r.fee).toFixed(2)}`} value={cur} onChange={(e) => setLevel(r.product_id, lvl, e.target.value)} className="h-7 w-24 rounded-sm text-xs" data-testid={`fee-level-input-${r.slug}-${lvl.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-[10px] text-[#6C757D] mt-1.5">Blank = use the standard fee. Click <b>Save</b> on the product row to apply.</div>
                </td>
              </tr>
            )}
            </React.Fragment>
          ); })}</tbody></table>)}
    </Card>
  );
}

function ClassGuidesTab({ vertical }) {
  const [guides, setGuides] = useState(null);
  const [products, setProducts] = useState([]);
  const [name, setName] = useState("");
  const fileRef = useRef(null);
  const load = async () => {
    try { const [g, p] = await Promise.all([api.get("/admin/class-guides"), api.get(`/admin/admin-fees?vertical=${vertical.value}`)]); setGuides(g.data); setProducts(p.data); }
    catch { toast.error("Could not load class guides"); }
  };
  useEffect(() => { load(); }, [vertical.value]); // eslint-disable-line react-hooks/exhaustive-deps
  const upload = async () => {
    const f = fileRef.current?.files?.[0]; if (!f || !name.trim()) { toast.error("Pick a file and give the guide a name"); return; }
    const fd = new FormData(); fd.append("file", f); fd.append("name", name.trim());
    try { await api.post("/admin/class-guides", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Class guide uploaded"); setName(""); fileRef.current.value = ""; load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const attach = async (g, productId, on) => {
    try { await api.post(`/admin/class-guides/${g.id}/attach`, { product_id: productId, attach: on }); toast.success(on ? "Attached — prints as the last page of that rate card" : "Detached"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const remove = async (g) => { if (!window.confirm(`Delete class guide "${g.name}"? It will be removed from any rate cards it's attached to.`)) return; try { await api.delete(`/admin/class-guides/${g.id}`); load(); } catch { toast.error("Delete failed"); } };
  return (
    <Card className="bg-white border border-[#E9ECEF] rounded-sm p-6" data-testid="class-guides-tab">
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B] mb-1">Class guides — {vertical.label}</div>
      <p className="text-xs text-[#6C757D] mb-4">Upload a make/model class guide (.xlsx with columns <b>Class · Make · Model / Category</b>) and attach it to any rate card. It prints as a final "Class guide" page, grouped by class in paragraph form. A guide attached to several cards stays in sync.</p>
      <div className="flex flex-wrap items-end gap-2 mb-5 p-3 border border-dashed border-[#0B132B]/20 rounded-sm">
        <div><Label className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D]">Guide name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Essentials New Car 2026" className="h-9 w-64 rounded-sm mt-1" data-testid="class-guide-name" /></div>
        <input ref={fileRef} type="file" accept=".xlsx" className="text-xs" data-testid="class-guide-file" />
        <Button size="sm" onClick={upload} className="h-9 rounded-sm bg-[#0B132B] text-white" data-testid="class-guide-upload"><Upload size={12} className="mr-1.5" /> Upload guide</Button>
      </div>
      {!guides ? <div className="text-sm text-[#6C757D]">Loading…</div> : guides.length === 0 ? <div className="text-xs italic text-[#6C757D]">No class guides uploaded yet.</div> : guides.map((g) => (
        <div key={g.id} className="border border-[#E9ECEF] rounded-sm p-4 mb-3" data-testid={`class-guide-${g.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div><div className="font-bold text-[#0B132B]">{g.name}</div><div className="text-[11px] text-[#6C757D]">{g.rows} rows · classes {g.classes.join(", ")} · {g.filename}</div></div>
            <Button size="sm" variant="outline" onClick={() => remove(g)} className="h-8 rounded-sm text-red-700 border-red-200"><Trash2 size={12} /></Button>
          </div>
          <div className="mt-3 text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D] mb-1">Attached to rate cards</div>
          <div className="flex flex-wrap gap-1.5">{products.map((p) => { const on = g.attached_product_ids.includes(p.product_id); return (
            <button key={p.product_id} type="button" onClick={() => attach(g, p.product_id, !on)} className={`text-[11px] px-2 py-1 rounded-sm border ${on ? "bg-[#0B132B] text-white border-[#0B132B]" : "border-[#E9ECEF] text-[#0B132B] hover:border-[#0B132B]/40"}`} data-testid={`guide-attach-${g.id}-${p.slug}`}>{p.name}</button>); })}</div>
        </div>))}
    </Card>
  );
}

function EngineDetails({ info, onChanged }) {
  return (
    <div className="space-y-6" data-testid="engine-details">
      {info?.engine && (
        <Card className="bg-white border border-[#E9ECEF] rounded-sm p-6" data-testid="admin-rates-info-card">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B] mb-4">Live rate engine</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <InfoStat label="Rate entries" value={info.engine.rate_entries} /><InfoStat label="Products" value={info.engine.products} /><InfoStat label="Class guide makes" value={info.engine.class_guide_makes} /><InfoStat label="Terms" value={info.engine.terms?.length} />
          </div>
          <div className="mt-4 text-xs text-[#6C757D]"><span className="font-semibold text-[#0B132B]">Source:</span> <span className="font-mono">{info.engine.source_path || "—"}</span></div>
        </Card>)}
      <Collapse title="Products loaded" testId="engine-products">{info?.products && <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{info.products.map((p) => <ProductLoadedCard key={p.key} product={p} onChanged={onChanged} />)}</div>}</Collapse>
      <Collapse title="Class overrides" testId="engine-overrides"><ClassOverridesPanel onChange={onChanged} /></Collapse>
      <Collapse title="Class guide (makes not listed → ineligible)" testId="engine-class-guide">
        {info?.class_guide && <div className="grid grid-cols-1 md:grid-cols-5 gap-4">{info.class_guide.map((g) => <div key={g.class} className="border border-[#E9ECEF] rounded-sm p-4"><div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#D4AF37]">Class {g.class}</div><ul className="mt-2 space-y-1 text-sm text-[#0B132B]">{g.makes.map((m) => <li key={m}>{m}</li>)}</ul></div>)}</div>}
      </Collapse>
      {info?.wearguard_class_guide?.some((g) => g.makes.length > 0) && (
        <Collapse title="Wear Guard class map" testId="engine-wearguard">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{info.wearguard_class_guide.map((g) => <div key={g.class} className="border border-[#E9ECEF] rounded-sm p-4" data-testid={`wg-class-${g.class}`}><div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#D4AF37]">Class {g.class} · {g.class === "A" ? "Mass market" : "Luxury"} · {g.makes.length} makes</div><ul className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-sm text-[#0B132B]">{g.makes.map((m) => <li key={m}>{m}</li>)}</ul></div>)}</div>
        </Collapse>)}
    </div>
  );
}

function Collapse({ title, children, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="bg-white border border-[#E9ECEF] rounded-sm" data-testid={testId}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="w-full flex items-center justify-between px-6 py-4 text-left" data-testid={`${testId}-toggle`}>
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">{title}</span>{open ? <ChevronUp size={14} className="text-[#6C757D]" /> : <ChevronDown size={14} className="text-[#6C757D]" />}
      </button>
      {open && <div className="px-6 pb-6">{children}</div>}
    </Card>
  );
}

function InfoStat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#6C757D]">{label}</div>
      <div className="font-display text-3xl font-black text-[#0B132B] mt-1 font-mono">{value ?? "—"}</div>
    </div>
  );
}

// ============================================================================
// Rate Library — sheets by (vertical × category), with admin-managed categories
// ============================================================================
function RateLibrarySection({ vertical, onChanged }) {
  const [sheets, setSheets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const shownVerticals = vertical ? VERTICALS.filter((v) => v.value === vertical) : VERTICALS;

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/admin/rate-library/sheets"),
        api.get("/rate-library/categories"),
      ]);
      setSheets(s.data || []);
      setCategories(c.data || []);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const grid = useMemo(() => {
    const g = {};
    for (const v of VERTICALS) g[v.value] = {};
    // Regular Admin Rates library page hides production-only cards on
    // purpose — those live at /admin/production-rate-cards (internal-only).
    for (const s of sheets) {
      if (s.production_only) continue;
      const v = s.vertical; const c = s.category;
      if (!g[v]) g[v] = {};
      if (!g[v][c]) g[v][c] = [];
      g[v][c].push(s);
    }
    return g;
  }, [sheets]);

  const removeSheet = async (s) => {
    if (!window.confirm(`Delete rate sheet "${s.program_name}"?`)) return;
    try {
      await api.delete(`/admin/rate-library/sheets/${s.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const duplicateSheet = async (s) => {
    try {
      await api.post(`/admin/rate-library/sheets/${s.id}/duplicate`);
      toast.success(`Duplicated "${s.program_name}"`);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const [historyFor, setHistoryFor] = useState(null);

  const downloadSheet = async (s, disposition = "attachment") => {
    try {
      const { data, headers } = await api.get(`/rate-library/sheets/${s.id}/file`, {
        params: { disposition },
        responseType: "blob",
      });
      const blob = new Blob([data], { type: headers["content-type"] || s.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      if (disposition === "inline") {
        // Open in a new tab — browsers render PDFs, xlsx will still download.
        const w = window.open(url, "_blank", "noopener");
        if (!w) toast.error("Popup blocked — allow popups to preview.");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = s.filename || "rate-sheet.xlsx";
        a.click();
      }
      // Delay revoke so the popup has time to load.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const replaceSheet = async (s, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/rate-library/sheets/${s.id}/file`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Updated "${s.program_name}" with new file`);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <Card className="bg-white border border-[#E9ECEF] rounded-sm p-8" data-testid="admin-rate-library-card">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <Library size={16} className="text-[#D4AF37]" />
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">Rate cards{vertical ? "" : " — all verticals"}</div>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={catOpen} onOpenChange={setCatOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="rounded-sm h-9" data-testid="admin-rate-library-categories-btn">
                <Tag size={12} className="mr-1.5" /> Manage categories
              </Button>
            </DialogTrigger>
            <CategoriesDialog categories={categories} onChanged={load} />
          </Dialog>
          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="rounded-sm bg-[#0B132B] text-white hover:bg-[#0B132B]/90 h-9" data-testid="admin-rate-library-upload-btn">
                <Upload size={12} className="mr-1.5" /> Upload rate sheet
              </Button>
            </DialogTrigger>
            <UploadSheetDialog categories={categories} defaultVertical={vertical} onUploaded={() => { setUploadOpen(false); load(); }} />
          </Dialog>
          <Dialog open={manualOpen} onOpenChange={setManualOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="rounded-sm h-9 border-[#D4AF37] text-[#0B132B] hover:bg-[#D4AF37]/10" data-testid="admin-rate-library-manual-btn">
                <Plus size={12} className="mr-1.5" /> Create manually
              </Button>
            </DialogTrigger>
            <ManualProgramDialog categories={categories} onSaved={() => { setManualOpen(false); load(); }} />
          </Dialog>
        </div>
      </div>
      {/* Change-log dialog — one per row via `historyFor` state. */}
      <Dialog open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
        {historyFor && <ChangeLogDialog sheet={historyFor} />}
      </Dialog>
      <p className="text-xs text-[#6C757D] mb-5">
        Upload, replace or retire rate cards by <b>category</b>. Tag a sheet to the live engine and it feeds proposals and the rate-card generator automatically.
      </p>

      {loading ? (
        <div className="text-sm text-[#6C757D]"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading library…</div>
      ) : (
        <div className={`grid grid-cols-1 ${vertical ? "" : "md:grid-cols-2"} gap-4`} data-testid="admin-rate-library-grid">
          {shownVerticals.map((v) => {
            const V = v.icon;
            const catsInV = Object.keys(grid[v.value] || {});
            const total = catsInV.reduce((s, c) => s + (grid[v.value][c]?.length || 0), 0);
            return (
              <Card key={v.value} className="p-4 border-[#E9ECEF] rounded-sm" data-testid={`rate-lib-vertical-${v.value}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <V size={16} className="text-[#D4AF37]" />
                    <div className="font-bold text-[#0B132B]">{v.label}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider font-bold text-[#6C757D]">{total} sheet{total !== 1 && "s"}</span>
                </div>
                {catsInV.length === 0 ? (
                  <div className="text-xs text-[#6C757D] italic py-2">No rate sheets uploaded for {v.label}.</div>
                ) : (
                  <div className="space-y-3">
                    {catsInV.map((cat) => (
                      <div key={cat} data-testid={`rate-lib-${v.value}-${cat.toLowerCase().replace(/ /g, "-")}`}>
                        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#D4AF37] mb-1">{cat}</div>
                        <div className="space-y-1">
                          {grid[v.value][cat].map((s) => (
                            <SheetRow
                              key={s.id}
                              sheet={s}
                              onView={() => downloadSheet(s, "inline")}
                              onDownload={() => downloadSheet(s)}
                              onReplace={(file) => replaceSheet(s, file)}
                              onDelete={() => removeSheet(s)}
                              onDuplicate={() => duplicateSheet(s)}
                              onHistory={() => setHistoryFor(s)}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function SheetRow({ sheet, onView, onDownload, onReplace, onDelete, onDuplicate, onHistory }) {
  const replaceRef = useRef(null);
  const isPdf = (sheet.content_type || "").toLowerCase() === "application/pdf" || (sheet.filename || "").toLowerCase().endsWith(".pdf");
  const replacedNote = sheet.replaced_at ? ` · updated ${new Date(sheet.replaced_at).toLocaleDateString()}` : "";
  return (
    <div className="flex items-center gap-2 p-2 bg-[#F8F9FA] border border-[#E9ECEF] rounded-sm" data-testid={`rate-lib-sheet-${sheet.id}`}>
      <FileSpreadsheet size={12} className="text-[#0B132B]/70 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[#0B132B] truncate flex items-center gap-2">
          <span className="truncate">{sheet.program_name}</span>
          {sheet.engine_kind && (
            <span
              className="text-[9px] uppercase tracking-wider font-bold text-[#0B132B] bg-[#D4AF37]/20 border border-[#D4AF37]/50 px-1.5 py-0.5 rounded-sm shrink-0"
              title={`Live proposal source — ${sheet.engine_kind_label || sheet.engine_kind}`}
              data-testid={`rate-lib-sheet-engine-${sheet.id}`}
            >Live · {sheet.engine_kind}</span>
          )}
          {sheet.manual && (
            <span
              className="text-[9px] uppercase tracking-wider font-bold text-[#0B132B] bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-sm shrink-0"
              title={`Manually entered — ${sheet.manual_summary?.cell_count || 0} price cells`}
              data-testid={`rate-lib-sheet-manual-${sheet.id}`}
            >Manual</span>
          )}
          {sheet.production_only && (
            <span
              className="text-[9px] uppercase tracking-wider font-bold text-white bg-[#0B132B] border border-[#0B132B] px-1.5 py-0.5 rounded-sm shrink-0"
              title="Production-only — hidden from agents"
              data-testid={`rate-lib-sheet-production-${sheet.id}`}
            >Production</span>
          )}
        </div>
        <div className="text-[10px] text-[#6C757D] truncate">
          {sheet.manual ? (
            <>
              Manual · {sheet.manual_summary?.rows?.length || 0} row{(sheet.manual_summary?.rows?.length || 0) === 1 ? "" : "s"}
              {" × "}
              {sheet.manual_summary?.columns?.length || 0} col{(sheet.manual_summary?.columns?.length || 0) === 1 ? "" : "s"}
              {" · "}
              {sheet.manual_summary?.cell_count || 0} price{(sheet.manual_summary?.cell_count || 0) === 1 ? "" : "s"} · created {new Date(sheet.uploaded_at).toLocaleDateString()}
            </>
          ) : (
            <>
              {sheet.filename} · uploaded {new Date(sheet.uploaded_at).toLocaleDateString()}{replacedNote}
            </>
          )}
        </div>
      </div>
      {!sheet.manual && (
        <Button
          size="icon" variant="outline" onClick={onView}
          className="h-7 w-7 rounded-sm" title={isPdf ? "View in browser" : "Open (xlsx will download)"}
          data-testid={`rate-lib-sheet-view-${sheet.id}`}
        >
          <Eye size={11} />
        </Button>
      )}
      {!sheet.manual && (
        <Button
          size="icon" variant="outline" onClick={onDownload}
          className="h-7 w-7 rounded-sm" title="Download"
          data-testid={`rate-lib-sheet-download-${sheet.id}`}
        >
          <Download size={11} />
        </Button>
      )}
      {!sheet.manual && (
        <input
          ref={replaceRef} type="file" accept=".xlsx,.xls,.pdf,.csv"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onReplace(f);
            if (replaceRef.current) replaceRef.current.value = "";
          }}
          data-testid={`rate-lib-sheet-replace-input-${sheet.id}`}
        />
      )}
      {!sheet.manual && (
        <Button
          size="icon" variant="outline" onClick={() => replaceRef.current?.click()}
          className="h-7 w-7 rounded-sm border-[#D4AF37] text-[#0B132B] hover:bg-[#D4AF37]/10"
          title="Replace with updated rates"
          data-testid={`rate-lib-sheet-replace-${sheet.id}`}
        >
          <RefreshCw size={11} />
        </Button>
      )}
      <Button
        size="icon" variant="outline" onClick={onDuplicate}
        className="h-7 w-7 rounded-sm" title="Duplicate — clone rows/columns/prices into a new program"
        data-testid={`rate-lib-sheet-duplicate-${sheet.id}`}
      >
        <Copy size={11} />
      </Button>
      <Button
        size="icon" variant="outline" onClick={onHistory}
        className="h-7 w-7 rounded-sm" title="Change history"
        data-testid={`rate-lib-sheet-history-${sheet.id}`}
      >
        <History size={11} />
      </Button>
      <Button
        size="icon" variant="outline" onClick={onDelete}
        className="h-7 w-7 rounded-sm border-red-300 text-red-700 hover:bg-red-50" title="Delete"
        data-testid={`rate-lib-sheet-delete-${sheet.id}`}
      >
        <Trash2 size={11} />
      </Button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Program Sync Hub — one place per program showing "rates loaded" health +
// a deep-link into marketing catalog scoped to that program. Keeps rates
// and collateral in lock-step per admin's "one place feeds every place" spec.
// -----------------------------------------------------------------------------
function ProgramSyncHub({ info }) {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/marketing/materials");
        const map = {};
        (data || []).forEach((m) => {
          const p = m.program || "general";
          map[p] = (map[p] || 0) + 1;
        });
        setCounts(map);
      } catch { /* non-fatal */ }
    })();
  }, []);

  const engineHas = (kind) => {
    if (!info?.engine) return false;
    // engine.info exposes per-family counts; a program is "live" if it has
    // any rate entries. Fall back to product presence.
    const map = info.engine.per_kind || {};
    if (map[kind]) return (map[kind].entries || 0) > 0;
    if (info.products) return info.products.some((p) => (p.key || "").startsWith(kind));
    return false;
  };

  return (
    <Card className="bg-white border border-[#E9ECEF] rounded-sm p-6" data-testid="admin-program-hub">
      <div className="flex items-center gap-2 mb-1">
        <Layers size={16} className="text-[#D4AF37]" />
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">
          Program hub — rates + marketing in sync
        </div>
      </div>
      <p className="text-xs text-[#6C757D] mb-5">
        Each program has one live rate sheet feeding proposals + rate-card generator, and its own bucket of marketing collateral (brochures, table toppers, posters, desktop laminates) that agents can download or order hard copies of.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {PROGRAM_HUB.map((p) => {
          const materials = counts[p.kind] || 0;
          const ratesLive = engineHas(p.kind);
          return (
            <div key={p.kind}
              className={`group rounded-sm border border-[#E9ECEF] bg-gradient-to-br ${p.tone} p-4 hover:border-[#D4AF37]/60 transition-colors`}
              data-testid={`program-hub-${p.kind}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-bold text-[#0B132B] leading-snug">{p.label}</div>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-sm border font-bold ${ratesLive ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-500"}`}>
                  {ratesLive ? "RATES LIVE" : "NO RATES"}
                </span>
              </div>
              <div className="text-[11px] text-[#6C757D] mt-2 flex items-center gap-1.5">
                <Package size={11} className="text-[#0B132B]/60" />
                <span><b className="text-[#0B132B]">{materials}</b> marketing material{materials === 1 ? "" : "s"}</span>
              </div>
              <div className="mt-3">
                <Link to={`/admin/marketing?program=${p.kind}`}>
                  <Button size="sm" variant="outline"
                    className="h-8 rounded-sm w-full text-[11px] uppercase tracking-[0.14em] font-bold border-[#0B132B]/20 hover:bg-[#0B132B] hover:text-white hover:border-[#0B132B]"
                    data-testid={`program-hub-manage-${p.kind}`}
                  >
                    Manage materials <ExternalLink size={11} className="ml-1.5" />
                  </Button>
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// essentials tiers live in essentials.xlsx) so the delete confirm calls that
// out explicitly.
// -----------------------------------------------------------------------------
const PRODUCT_KIND_LABEL = {
  essentials: "Essentials family (Powertrain, Plus, Enhanced, Exclusionary)",
  blended:    "Blended VSC family",
  franchise:  "Franchise Exclusionary",
  ancillary:  "Ancillary family (Bundle, Tire & Wheel, PDR, Key, Windshield)",
  wear_guard: "Wear Guard",
  gap:        "GAP",
  newcar:     "Essentials New Car (Exclusionary, Enhanced, Plus, Essentials)",
};

function _productSourceKind(key) {
  if (!key) return null;
  if (key.startsWith("newcar"))     return "newcar";
  if (key.startsWith("essentials")) return "essentials";
  if (key.startsWith("blended"))    return "blended";
  if (key.startsWith("franchise"))  return "franchise";
  if (key.startsWith("ancillary"))  return "ancillary";
  if (key === "wear_guard")         return "wear_guard";
  if (key === "gap")                return "gap";
  return null;
}

function ProductLoadedCard({ product, onChanged }) {
  const sourceKind = _productSourceKind(product.key);
  const replaceRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const view = async () => {
    if (!sourceKind) { toast.error("No source workbook for this product."); return; }
    setBusy(true);
    try {
      const { data, headers } = await api.get(`/admin/rates/source`, {
        params: { kind: sourceKind, disposition: "inline" },
        responseType: "blob",
      });
      const blob = new Blob([data], { type: headers["content-type"] || "application/pdf" });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank", "noopener");
      if (!w) toast.error("Popup blocked — allow popups to preview.");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const replace = async (file) => {
    if (!file || !sourceKind) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", sourceKind);
    try {
      await api.post("/admin/rates/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Replaced ${PRODUCT_KIND_LABEL[sourceKind] || sourceKind} rates`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!sourceKind) return;
    const label = PRODUCT_KIND_LABEL[sourceKind] || sourceKind;
    if (!window.confirm(`Delete ${label}? This removes the workbook from the engine, the matching rate card in the library, and its rate-card generator entries. Re-upload to re-enable.`)) return;
    setBusy(true);
    try {
      await api.delete("/admin/rates/source", { params: { kind: sourceKind } });
      toast.success(`Cleared ${label} rates`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-[#E9ECEF] rounded-sm p-4 flex flex-col" data-testid={`product-loaded-${product.key}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[#0B132B]">{product.label}</div>
          <div className="text-[11px] text-[#6C757D] mt-1 leading-snug">{product.description}</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] font-bold mt-2 font-mono">
            {product.key}
          </div>
        </div>
        {product.available === false && (
          <span className="text-[10px] uppercase tracking-wider text-red-700 border border-red-200 bg-red-50 px-1.5 py-0.5 rounded-sm">Unavailable</span>
        )}
      </div>
      {sourceKind && (
        <div className="mt-3 pt-3 border-t border-[#F1F3F5] flex items-center gap-1.5">
          <input
            ref={replaceRef} type="file" accept=".xlsx"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) replace(f);
              if (replaceRef.current) replaceRef.current.value = "";
            }}
            data-testid={`product-replace-input-${product.key}`}
          />
          <Button size="sm" variant="outline" onClick={view} disabled={busy}
            className="h-8 rounded-sm text-xs flex-1"
            data-testid={`product-view-${product.key}`}>
            <Eye size={11} className="mr-1" /> View
          </Button>
          <Button size="sm" variant="outline" onClick={() => replaceRef.current?.click()} disabled={busy}
            className="h-8 rounded-sm text-xs flex-1 border-[#D4AF37] text-[#0B132B] hover:bg-[#D4AF37]/10"
            data-testid={`product-replace-${product.key}`}>
            <RefreshCw size={11} className="mr-1" /> Replace
          </Button>
          <Button size="sm" variant="outline" onClick={del} disabled={busy}
            className="h-8 rounded-sm text-xs border-red-300 text-red-700 hover:bg-red-50"
            title="Delete workbook"
            data-testid={`product-delete-${product.key}`}>
            <Trash2 size={11} />
          </Button>
        </div>
      )}
    </div>
  );
}



function UploadSheetDialog({ categories, onUploaded, defaultVertical }) {
  const [vertical, setVertical] = useState(defaultVertical || "automotive");
  const [category, setCategory] = useState(categories[0]?.name || "VSC");
  const [programName, setProgramName] = useState("");
  const [notes, setNotes] = useState("");
  const [engineKind, setEngineKind] = useState("none");
  const [engineKinds, setEngineKinds] = useState([]);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/rate-library/engine-kinds")
      .then(({ data }) => setEngineKinds(data?.kinds || []))
      .catch(() => setEngineKinds([]));
  }, []);

  const submit = async () => {
    if (!file) { toast.error("Select a file"); return; }
    if (!programName.trim()) { toast.error("Program name is required"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("vertical", vertical);
    fd.append("category", category);
    fd.append("program_name", programName.trim());
    if (notes.trim()) fd.append("notes", notes.trim());
    if (engineKind && engineKind !== "none") fd.append("engine_kind", engineKind);
    try {
      await api.post("/admin/rate-library/sheets", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(engineKind !== "none"
        ? "Rate sheet uploaded — proposal engine updated"
        : "Rate sheet uploaded");
      onUploaded();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const activeKind = engineKinds.find((k) => k.kind === engineKind);

  return (
    <DialogContent className="max-w-lg" data-testid="admin-rate-library-upload-dialog">
      <DialogHeader><DialogTitle>Upload rate sheet</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Vertical *</Label>
            <Select value={vertical} onValueChange={setVertical}>
              <SelectTrigger className="h-9 rounded-sm" data-testid="rate-lib-upload-vertical"><SelectValue /></SelectTrigger>
              <SelectContent>{VERTICALS.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Category *</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-9 rounded-sm" data-testid="rate-lib-upload-category"><SelectValue /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs">Program name *</Label>
          <Input value={programName} onChange={(e) => setProgramName(e.target.value)} placeholder="e.g. Essentials Powertrain" className="rounded-sm h-9" data-testid="rate-lib-upload-program-name" />
        </div>
        <div>
          <Label className="text-xs">Notes</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="rounded-sm text-sm" data-testid="rate-lib-upload-notes" />
        </div>
        <div>
          <Label className="text-xs">Feeds proposal engine</Label>
          <Select value={engineKind} onValueChange={setEngineKind}>
            <SelectTrigger className="h-9 rounded-sm" data-testid="rate-lib-upload-engine-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none" data-testid="rate-lib-upload-engine-kind-none">— Reference only (not fed to engine) —</SelectItem>
              {engineKinds.map((k) => (
                <SelectItem key={k.kind} value={k.kind} data-testid={`rate-lib-upload-engine-kind-${k.kind}`}>{k.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-[#6C757D] mt-1 leading-snug">
            {engineKind === "none"
              ? "Storage only — sheet won't affect proposal pricing."
              : activeKind?.current
                ? <>Replaces <b>{activeKind.current.program_name}</b> as the live source for this program.</>
                : "New source — proposal quotes will use this file's rates."}
          </p>
        </div>
        <div>
          <Label className="text-xs">File * (.xlsx or .pdf)</Label>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.pdf,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-xs mt-1 file:mr-3 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:bg-[#0B132B] file:text-white file:text-xs file:font-bold hover:file:bg-[#0B132B]/90"
            data-testid="rate-lib-upload-file-input" />
        </div>
      </div>
      <DialogFooter>
        <Button size="sm" className="bg-[#0B132B] text-white" onClick={submit} disabled={uploading} data-testid="rate-lib-upload-submit">
          {uploading ? <><Loader2 className="animate-spin mr-1.5" size={12} /> Uploading…</> : <><Upload size={12} className="mr-1.5" /> Upload</>}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function CategoriesDialog({ categories, onChanged }) {
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    try {
      await api.post("/admin/rate-library/categories", { name });
      setNewName("");
      toast.success("Category added");
      onChanged();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (c) => {
    if (c.seeded) { toast.error("Default categories can't be deleted."); return; }
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try {
      await api.delete(`/admin/rate-library/categories/${c.id}`);
      toast.success("Deleted");
      onChanged();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <DialogContent className="max-w-md" data-testid="admin-rate-categories-dialog">
      <DialogHeader><DialogTitle>Rate sheet categories</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Prepaid Maintenance"
            className="rounded-sm h-9" data-testid="rate-categories-new-input" onKeyDown={(e) => e.key === "Enter" && add()} />
          <Button size="sm" onClick={add} disabled={saving || !newName.trim()} className="bg-[#0B132B] text-white h-9" data-testid="rate-categories-add-btn">
            <Plus size={12} className="mr-1" /> Add
          </Button>
        </div>
        <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="rate-categories-list">
          {(categories || []).map((c) => (
            <div key={c.id} className="flex items-center gap-2 p-2 border border-[#E9ECEF] rounded-sm" data-testid={`rate-category-${c.id}`}>
              <Tag size={12} className="text-[#D4AF37]" />
              <span className="text-sm text-[#0B132B] flex-1">{c.name}</span>
              {c.seeded ? (
                <span className="text-[10px] uppercase tracking-wider text-[#6C757D]">Standard</span>
              ) : (
                <Button size="icon" variant="outline" onClick={() => remove(c)} className="h-7 w-7 rounded-sm border-red-300 text-red-700 hover:bg-red-50">
                  <Trash2 size={11} />
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </DialogContent>
  );
}


// -----------------------------------------------------------------------------
// Manual warranty program builder — no Excel required. Rows and columns are
// fully admin-defined so the same builder handles LW (1/1 · 3/3 · 6/6 by
// $5k / AVC), GoForever Powertrain (New / Preowned), GoForever Battery
// (Single / Multi / EV), or anything the admin invents. Presets seed common
// layouts; admins can add/remove rows and columns freely. Wiring to the
// quote engine itself is Phase 2 — this delivers the "define programs
// without an xlsx" data-entry flow first.
// -----------------------------------------------------------------------------
const MANUAL_PRESETS = [
  {
    key: "lw",
    label: "Limited Warranty",
    rows: ["1/1", "3/3", "6/6"],
    columns: ["$5k", "AVC"],
  },
  {
    key: "gf_powertrain",
    label: "GoForever Powertrain",
    rows: [""],
    columns: ["New", "Preowned"],
  },
  {
    key: "gf_battery",
    label: "GoForever Battery",
    rows: [""],
    columns: ["Single Battery", "Multi Battery", "EV"],
  },
];

function ManualProgramDialog({ categories, onSaved }) {
  const [vertical, setVertical] = useState("automotive");
  const [category, setCategory] = useState(categories[0]?.name || "VSC");
  const [programName, setProgramName] = useState("");
  const [notes, setNotes] = useState("");
  const [rows, setRows] = useState([""]);
  const [columns, setColumns] = useState([""]);
  const [prices, setPrices] = useState({});
  const [saving, setSaving] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");

  const applyPreset = (key) => {
    const preset = MANUAL_PRESETS.find((p) => p.key === key);
    if (!preset) return;
    setRows(preset.rows.length ? [...preset.rows] : [""]);
    setColumns(preset.columns.length ? [...preset.columns] : [""]);
    setPrices({});
    if (!programName.trim()) setProgramName(preset.label);
  };

  const setRow = (i, v) => setRows((prev) => prev.map((r, idx) => (idx === i ? v : r)));
  const addRow = () => setRows((prev) => [...prev, ""]);
  const removeRow = (i) => {
    setRows((prev) => prev.filter((_, idx) => idx !== i));
    // Drop prices in the removed row and shift indices for rows below.
    setPrices((prev) => {
      const next = {};
      for (const [key, val] of Object.entries(prev)) {
        const m = /^r(\d+)_c(\d+)$/.exec(key);
        if (!m) continue;
        const ri = Number(m[1]);
        const ci = Number(m[2]);
        if (ri === i) continue;
        const newRi = ri > i ? ri - 1 : ri;
        next[`r${newRi}_c${ci}`] = val;
      }
      return next;
    });
  };

  const setCol = (i, v) => setColumns((prev) => prev.map((c, idx) => (idx === i ? v : c)));
  const addCol = () => setColumns((prev) => [...prev, ""]);
  const removeCol = (i) => {
    setColumns((prev) => prev.filter((_, idx) => idx !== i));
    setPrices((prev) => {
      const next = {};
      for (const [key, val] of Object.entries(prev)) {
        const m = /^r(\d+)_c(\d+)$/.exec(key);
        if (!m) continue;
        const ri = Number(m[1]);
        const ci = Number(m[2]);
        if (ci === i) continue;
        const newCi = ci > i ? ci - 1 : ci;
        next[`r${ri}_c${newCi}`] = val;
      }
      return next;
    });
  };

  const cellKey = (ri, ci) => `r${ri}_c${ci}`;
  const setPrice = (ri, ci, v) => {
    const key = cellKey(ri, ci);
    const num = parseFloat(v);
    setPrices((prev) => {
      const next = { ...prev };
      if (!v || !Number.isFinite(num) || num <= 0) delete next[key];
      else next[key] = num;
      return next;
    });
  };

  const filledRows = rows.map((r, i) => ({ i, label: (r || "").trim() })).filter((r) => r.label);
  const filledCols = columns.map((c, i) => ({ i, label: (c || "").trim() })).filter((c) => c.label);
  const cellCount = Object.keys(prices).length;
  const totalCells = filledRows.length * filledCols.length;

  const submit = async () => {
    if (!programName.trim()) { toast.error("Program name is required"); return; }
    if (!filledRows.length) { toast.error("Add at least one row label"); return; }
    if (!filledCols.length) { toast.error("Add at least one column label"); return; }
    if (!cellCount) { toast.error("Enter at least one rate"); return; }
    setSaving(true);
    try {
      // Compact rows/cols to only the filled labels + remap prices to the
      // compacted indexes so blank rows/cols don't get sent as gaps.
      const rowIndexMap = new Map(filledRows.map((r, newIdx) => [r.i, newIdx]));
      const colIndexMap = new Map(filledCols.map((c, newIdx) => [c.i, newIdx]));
      const compactedPrices = {};
      for (const [key, val] of Object.entries(prices)) {
        const m = /^r(\d+)_c(\d+)$/.exec(key);
        if (!m) continue;
        const ri = rowIndexMap.get(Number(m[1]));
        const ci = colIndexMap.get(Number(m[2]));
        if (ri === undefined || ci === undefined) continue;
        compactedPrices[`r${ri}_c${ci}`] = val;
      }
      await api.post("/admin/rate-library/manual", {
        vertical,
        category,
        program_name: programName.trim(),
        notes: notes.trim() || null,
        rows: filledRows.map((r) => r.label),
        columns: filledCols.map((c) => c.label),
        prices: compactedPrices,
      });
      toast.success(`Manual program saved (${Object.keys(compactedPrices).length} price cells)`);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-4xl max-h-[92vh] flex flex-col" data-testid="admin-rate-manual-dialog">
      <DialogHeader>
        <DialogTitle>Create a rate program manually</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 overflow-y-auto pr-1">
        {/* Presets */}
        <div className="border border-[#E9ECEF] rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B] mb-2">Quick-start templates</div>
          <div className="flex flex-wrap gap-1.5">
            {MANUAL_PRESETS.map((p) => (
              <button key={p.key} type="button" onClick={() => applyPreset(p.key)}
                className="h-8 px-3 text-xs font-bold rounded-sm border border-[#D4AF37] text-[#0B132B] bg-white hover:bg-[#D4AF37]/10"
                data-testid={`rate-lib-manual-preset-${p.key}`}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="text-[10px] text-[#6C757D] mt-1.5 leading-snug">
            Presets seed rows and columns; you can add, rename, or delete any of them before saving. Prices always start blank.
          </div>
        </div>

        {/* Metadata */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Vertical *</Label>
            <Select value={vertical} onValueChange={setVertical}>
              <SelectTrigger className="h-9 rounded-sm" data-testid="rate-lib-manual-vertical"><SelectValue /></SelectTrigger>
              <SelectContent>
                {VERTICALS.map((v) => (<SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Category *</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-9 rounded-sm" data-testid="rate-lib-manual-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(categories || []).map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Program name *</Label>
            <Input value={programName} onChange={(e) => setProgramName(e.target.value)} placeholder="e.g. Custom Preowned VSC — Q1"
              className="h-9 rounded-sm" data-testid="rate-lib-manual-name" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="rounded-sm text-sm" data-testid="rate-lib-manual-notes" />
          </div>
        </div>

        {/* Rows + Columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="border border-[#E9ECEF] rounded-sm p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Rows</div>
              <Button size="sm" variant="outline" onClick={addRow} className="h-7 rounded-sm text-xs" data-testid="rate-lib-manual-add-row">
                <Plus size={11} className="mr-1" /> Add row
              </Button>
            </div>
            <div className="space-y-1.5">
              {rows.map((r, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <Input value={r} onChange={(e) => setRow(i, e.target.value)} placeholder={`Row ${i + 1} (e.g. 1/1, 3/3)`}
                    className="h-8 rounded-sm text-sm" data-testid={`rate-lib-manual-row-${i}`} />
                  <Button size="icon" variant="outline" onClick={() => removeRow(i)}
                    disabled={rows.length === 1}
                    className="h-8 w-8 rounded-sm border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-40"
                    data-testid={`rate-lib-manual-row-delete-${i}`}>
                    <Trash2 size={11} />
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <div className="border border-[#E9ECEF] rounded-sm p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Columns</div>
              <Button size="sm" variant="outline" onClick={addCol} className="h-7 rounded-sm text-xs" data-testid="rate-lib-manual-add-col">
                <Plus size={11} className="mr-1" /> Add column
              </Button>
            </div>
            <div className="space-y-1.5">
              {columns.map((c, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <Input value={c} onChange={(e) => setCol(i, e.target.value)} placeholder={`Column ${i + 1} (e.g. $5k, AVC)`}
                    className="h-8 rounded-sm text-sm" data-testid={`rate-lib-manual-col-${i}`} />
                  <Button size="icon" variant="outline" onClick={() => removeCol(i)}
                    disabled={columns.length === 1}
                    className="h-8 w-8 rounded-sm border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-40"
                    data-testid={`rate-lib-manual-col-delete-${i}`}>
                    <Trash2 size={11} />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Rate matrix */}
        <div className="border border-[#E9ECEF] rounded-sm p-3">
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">Rate matrix</div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setPasteOpen(true)}
                disabled={!filledRows.length || !filledCols.length}
                className="h-7 rounded-sm text-xs disabled:opacity-40"
                data-testid="rate-lib-manual-paste-btn">
                <ClipboardPaste size={11} className="mr-1" /> Bulk paste
              </Button>
              <div className="text-[10px] text-[#6C757D]">
                {cellCount}/{totalCells} cells filled
              </div>
            </div>
          </div>
          {!filledRows.length || !filledCols.length ? (
            <div className="text-xs text-[#6C757D] italic py-4 text-center">
              Fill in at least one row label and one column label to unlock the matrix.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="rate-lib-manual-matrix">
                <thead>
                  <tr className="bg-[#0B132B] text-white text-[10px] uppercase tracking-wider">
                    <th className="text-left px-2 py-1.5 font-semibold min-w-[100px]">Row \ Col</th>
                    {filledCols.map((c) => (
                      <th key={c.i} className="text-left px-2 py-1.5 font-semibold">{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filledRows.map((r) => (
                    <tr key={r.i} className="border-b border-[#F1F3F5]">
                      <td className="px-2 py-1.5 font-semibold text-xs text-[#0B132B]">{r.label}</td>
                      {filledCols.map((c) => {
                        const key = cellKey(r.i, c.i);
                        return (
                          <td key={c.i} className="px-1 py-1">
                            <Input
                              type="number"
                              inputMode="decimal"
                              value={prices[key] ?? ""}
                              onChange={(e) => setPrice(r.i, c.i, e.target.value)}
                              placeholder="—"
                              className="h-8 rounded-sm text-xs font-mono"
                              data-testid={`rate-lib-manual-cell-${r.i}-${c.i}`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="text-[10px] text-[#6C757D] mt-2">
            Leave cells blank for combinations you don't offer. Only cells with a price &gt; 0 are saved.
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button size="sm" className="bg-[#0B132B] text-white" onClick={submit} disabled={saving} data-testid="rate-lib-manual-submit">
          {saving ? <><Loader2 className="animate-spin mr-1.5" size={12} /> Saving…</> : <><Plus size={12} className="mr-1.5" /> Save program</>}
        </Button>
      </DialogFooter>

      {/* Bulk-paste sub-dialog — accepts TSV/CSV rectangular block, aligns
          top-left with the first filled row/col, ignores overflow. */}
      <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
        <DialogContent className="max-w-lg" data-testid="rate-lib-manual-paste-dialog">
          <DialogHeader>
            <DialogTitle>Bulk paste rates</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-[#6C757D] leading-snug">
              Paste a rectangular block copied from Excel / Google Sheets. Rows are separated by newlines; columns by TAB or comma.
              The block fills top-left starting at row 1 × column 1. Non-numeric or 0 values leave the cell blank.
            </p>
            <Textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              rows={8}
              placeholder={"150\t175\n400\t475\n650\t775"}
              className="rounded-sm text-xs font-mono"
              data-testid="rate-lib-manual-paste-textarea"
            />
            <div className="text-[10px] text-[#6C757D]">
              Matrix accepts {filledRows.length} row{filledRows.length === 1 ? "" : "s"} × {filledCols.length} col{filledCols.length === 1 ? "" : "s"} — extra cells in the paste are ignored.
            </div>
          </div>
          <DialogFooter>
            <Button
              size="sm"
              className="bg-[#0B132B] text-white"
              onClick={() => {
                const lines = (pasteText || "").split(/\r?\n/).filter((l) => l.trim().length);
                let filled = 0;
                const next = { ...prices };
                for (let ri = 0; ri < lines.length && ri < filledRows.length; ri++) {
                  // Prefer TAB (Excel default); fall back to comma or semicolon.
                  const cells = /\t/.test(lines[ri])
                    ? lines[ri].split(/\t/)
                    : lines[ri].split(/[,;]/);
                  for (let ci = 0; ci < cells.length && ci < filledCols.length; ci++) {
                    const raw = String(cells[ci] || "").replace(/[$\s,]/g, "");
                    const num = parseFloat(raw);
                    const key = cellKey(filledRows[ri].i, filledCols[ci].i);
                    if (!Number.isFinite(num) || num <= 0) {
                      delete next[key];
                    } else {
                      next[key] = num;
                      filled++;
                    }
                  }
                }
                setPrices(next);
                setPasteOpen(false);
                setPasteText("");
                toast.success(`Filled ${filled} cell${filled === 1 ? "" : "s"} from paste`);
              }}
              disabled={!pasteText.trim()}
              data-testid="rate-lib-manual-paste-apply"
            >
              <ClipboardPaste size={12} className="mr-1.5" /> Apply paste
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DialogContent>
  );
}


// -----------------------------------------------------------------------------
// Change-log modal — reinsurance audit trail (who / when / what) for every
// mutation on a rate library entry (create, duplicate, delete). Read-only.
// -----------------------------------------------------------------------------
function ChangeLogDialog({ sheet }) {
  const [entries, setEntries] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/rate-library/sheets/${sheet.id}/change-log`);
        if (!cancelled) setEntries(data || []);
      } catch (e) {
        if (!cancelled) {
          setEntries([]);
          toast.error(formatApiError(e?.response?.data?.detail));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [sheet.id]);

  return (
    <DialogContent className="max-w-lg" data-testid="rate-lib-change-log-dialog">
      <DialogHeader>
        <DialogTitle>Change log — {sheet.program_name}</DialogTitle>
      </DialogHeader>
      {entries === null ? (
        <div className="text-sm text-[#6C757D] py-6 text-center">
          <Loader2 className="animate-spin inline mr-2" size={13} /> Loading…
        </div>
      ) : entries.length === 0 ? (
        <div className="text-sm text-[#6C757D] italic py-6 text-center">
          No changes tracked yet. Manual programs record every create, duplicate, and delete going forward.
        </div>
      ) : (
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1" data-testid="rate-lib-change-log-list">
          {entries.map((e) => {
            const when = e.at ? new Date(e.at) : null;
            const actionColor = e.action === "created" ? "bg-emerald-50 border-emerald-200 text-emerald-700"
              : e.action === "duplicated" ? "bg-blue-50 border-blue-200 text-blue-700"
              : e.action === "deleted" ? "bg-red-50 border-red-200 text-red-700"
              : "bg-[#F8F9FA] border-[#E9ECEF] text-[#0B132B]";
            return (
              <div key={e.id} className="border border-[#E9ECEF] rounded-sm p-2.5 flex flex-col gap-1" data-testid={`rate-lib-change-log-entry-${e.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm border ${actionColor}`}>
                    {e.action}
                  </span>
                  <span className="text-xs font-semibold text-[#0B132B]">{e.by_name || e.by || "Unknown"}</span>
                  <span className="text-[10px] text-[#6C757D] ml-auto">
                    {when ? when.toLocaleString() : "—"}
                  </span>
                </div>
                {e.meta && Object.keys(e.meta).length > 0 && (
                  <div className="text-[11px] text-[#6C757D] font-mono">
                    {Object.entries(e.meta).map(([k, v]) => (
                      <span key={k} className="mr-3">
                        <span className="text-[#D4AF37]">{k}:</span> {String(v)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </DialogContent>
  );
}
